/**
 * Plot Matrix Generator
 *
 * This utility generates dynamic Plot Matrix columns based on actual groundworks step data.
 * It extracts columns that have values from each step and prepares them for rate calculation.
 */

import { generateRateFromFieldName } from './rateCalculationUtils';

interface GroundworksStepData {
  cutFillsTable?: any[];
  foundationsTable?: any[];
  drainageTable?: any[]; // Legacy field for backward compatibility
  drainageTableStandard?: any[]; // Primary drainage field
  plotDrivesTable?: any[];
  plotServiceTable?: any[];
  plotPavingsTable?: any[];
  scaffoldTable?: any[];
  gardenTable?: any[];
  retainingWallsTable?: any[];
  geoToberWallTable?: any[];
}

interface PlotMatrixColumn {
  key: string;
  stepName: string;
  fieldName: string;
  label: string;
  unit?: string;
  hasData: boolean;
  dataIndex: string;
  rate: number; // Random rate for now, will be manipulated later
}

interface PlotMatrixRow {
  plotNo: string;
  housetype: string;
  [key: string]: any; // Dynamic fields from each step
}

/**
 * Extract columns with values from a table
 */
const extractColumnsWithValues = (
  table: any[] | undefined,
  stepName: string,
  stepKey: string
): PlotMatrixColumn[] => {
  if (!table || table.length === 0) return [];

  const columns: PlotMatrixColumn[] = [];
  const sampleRow = table[0];

  // Exclude system fields
  const excludedFields = ["key", "id", "_id", "createdAt", "updatedAt"];

  Object.keys(sampleRow).forEach((fieldName) => {
    if (excludedFields.includes(fieldName)) return;

    // Only include NUMERIC fields (skip text fields like plotNo, housetype)
    const sampleValue = sampleRow[fieldName];
    const isNumericField =
      typeof sampleValue === "number" ||
      (!isNaN(parseFloat(sampleValue)) && sampleValue !== "");

    if (!isNumericField) return;

    // Check if any row has a non-zero value for this numeric field
    const hasData = table.some((row) => {
      const value = row[fieldName];
      if (value === undefined || value === null) return false;
      const numValue = typeof value === "number" ? value : parseFloat(value);
      return !isNaN(numValue) && numValue !== 0;
    });

    if (hasData) {
      // Generate a human-readable label
      const label = fieldName
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .trim();

      // Generate fixed rate using shared utility
      const fixedRate = generateRateFromFieldName(fieldName);

      columns.push({
        key: `${stepKey}_${fieldName}`,
        stepName,
        fieldName,
        label: `${label}`,
        hasData: true,
        dataIndex: `${stepKey}_${fieldName}`,
        rate: fixedRate,
      });
    }
  });

  return columns;
};

/**
 * Generate Plot Matrix columns from all groundworks steps
 */
export const generatePlotMatrixColumns = (
  groundworksData: GroundworksStepData
): PlotMatrixColumn[] => {
  const allColumns: PlotMatrixColumn[] = [];

  // Extract columns from each step
  const steps = [
    {
      data: groundworksData.cutFillsTable,
      name: "Cut & Fills",
      key: "cutFills",
    },
    {
      data: groundworksData.foundationsTable,
      name: "Foundations",
      key: "foundations",
    },
    {
      data: groundworksData.drainageTableStandard || groundworksData.drainageTable,
      name: "Drainage",
      key: "drainage"
    },
    {
      data: groundworksData.plotDrivesTable,
      name: "Plot Drives",
      key: "plotDrives",
    },
    {
      data: groundworksData.plotPavingsTable,
      name: "Plot Paving",
      key: "plotPavings",
    },
    { data: groundworksData.scaffoldTable, name: "Scaffold", key: "scaffold" },
    { data: groundworksData.gardenTable, name: "Garden", key: "garden" },
    // POS Areas excluded - not plot-specific data
    {
      data: groundworksData.retainingWallsTable,
      name: "Ret & Screen Walls",
      key: "retainingWalls",
    },
    {
      data: groundworksData.geoToberWallTable,
      name: "Geowall & Tobermore Walls",
      key: "geoToberWall",
    },
  ];

  steps.forEach(({ data, name, key }) => {
    const stepColumns = extractColumnsWithValues(data, name, key);
    allColumns.push(...stepColumns);
  });

  return allColumns;
};

/**
 * Generate Plot Matrix rows by combining data from all steps
 */
export const generatePlotMatrixRows = (
  groundworksData: GroundworksStepData,
  columns: PlotMatrixColumn[]
): PlotMatrixRow[] => {
  // Get unique plot numbers from all steps
  const plotNumbers = new Set<string>();
  const plotHousetypes = new Map<string, string>();

  // Exclude plotServiceTable since it doesn't have plotNo field
  const allTables = [
    groundworksData.cutFillsTable,
    groundworksData.foundationsTable,
    groundworksData.drainageTableStandard || groundworksData.drainageTable,
    groundworksData.plotDrivesTable,
    groundworksData.plotPavingsTable,
    groundworksData.scaffoldTable,
    groundworksData.gardenTable,
    // plotServiceTable excluded - Plot Service doesn't have plotNo field
    groundworksData.retainingWallsTable,
    groundworksData.geoToberWallTable,
  ];

  allTables.forEach((table) => {
    if (table && table.length > 0) {
      table.forEach((row) => {
        const plotNo = row.plotNo || row["Plot No"] || row.PLOT_NO;
        const housetype = row.housetype || row["House Type"] || row.HOUSETYPE;

        if (plotNo && plotNo !== "Total" && plotNo !== "totals") {
          plotNumbers.add(plotNo.toString());
          if (housetype) {
            plotHousetypes.set(plotNo.toString(), housetype);
          }
        }
      });
    }
  });

  // Create rows
  const rows: PlotMatrixRow[] = [];

  plotNumbers.forEach((plotNo) => {
    const row: PlotMatrixRow = {
      key: `plot_${plotNo}`,
      plotNo: plotNo,
      housetype: plotHousetypes.get(plotNo) || "-",
    };

    // For each column, find the value from the corresponding step table
    columns.forEach((column) => {
      const [stepKey, fieldName] = column.key.split("_").slice(0, 2);
      const table = (groundworksData as any)[`${stepKey}Table`];

      if (table && table.length > 0) {
        const plotData = table.find(
          (r: any) =>
            (r.plotNo || r["Plot No"] || r.PLOT_NO)?.toString() === plotNo
        );

        if (plotData) {
          const value = plotData[fieldName];
          // Calculate: quantity * rate = total
          const quantity =
            typeof value === "number" ? value : parseFloat(value) || 0;
          const total = quantity * column.rate;

          row[column.dataIndex] = {
            quantity,
            rate: column.rate,
            total,
          };
        } else {
          row[column.dataIndex] = {
            quantity: 0,
            rate: column.rate,
            total: 0,
          };
        }
      } else {
        row[column.dataIndex] = {
          quantity: 0,
          rate: column.rate,
          total: 0,
        };
      }
    });

    rows.push(row);
  });

  return rows;
};

/**
 * Calculate row total
 */
export const calculateRowTotal = (row: PlotMatrixRow): number => {
  let total = 0;

  Object.keys(row).forEach((key) => {
    if (key !== "key" && key !== "plotNo" && key !== "housetype") {
      const cellData = row[key];
      if (cellData && typeof cellData === "object" && "total" in cellData) {
        total += cellData.total || 0;
      }
    }
  });

  return total;
};

/**
 * Update rate for a specific column
 */
export const updateColumnRate = (
  rows: PlotMatrixRow[],
  columnDataIndex: string,
  newRate: number
): PlotMatrixRow[] => {
  return rows.map((row) => {
    if (row[columnDataIndex]) {
      const quantity = row[columnDataIndex].quantity;
      return {
        ...row,
        [columnDataIndex]: {
          quantity,
          rate: newRate,
          total: quantity * newRate,
        },
      };
    }
    return row;
  });
};

/**
 * Update quantity for a specific cell
 */
export const updateCellQuantity = (
  rows: PlotMatrixRow[],
  plotNo: string,
  columnDataIndex: string,
  newQuantity: number
): PlotMatrixRow[] => {
  return rows.map((row) => {
    if (row.plotNo === plotNo && row[columnDataIndex]) {
      const rate = row[columnDataIndex].rate;
      return {
        ...row,
        [columnDataIndex]: {
          quantity: newQuantity,
          rate,
          total: newQuantity * rate,
        },
      };
    }
    return row;
  });
};
